import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";

export type DropdownSelectorVariant = "outlined" | "text";

interface VariantStyle {
  trigger: SxProps<Theme>;
  label: SxProps<Theme>;
  renderIcon: () => React.ReactNode;
}

// Add an entry here to introduce a new trigger look without touching the component markup.
const VARIANT_STYLES: Record<DropdownSelectorVariant, VariantStyle> = {
  outlined: {
    trigger: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      height: 32,
      pl: 1.5,
      pr: 1,
      border: "1px solid var(--color-ink)",
      borderRadius: "16px",
      color: "var(--color-ink)",
    },
    label: {
      fontFamily: "EnterpriseSansVF, Arial, sans-serif",
      fontWeight: 700,
      color: "var(--color-ink)",
      fontSize: "14px",
      whiteSpace: "nowrap",
    },
    renderIcon: () => <ExpandMoreIcon fontSize="small" sx={{ color: "var(--color-ink)" }} />,
  },
  text: {
    trigger: {
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 0.5,
      py: 0.25,
      borderRadius: 1,
      color: "var(--color-ink)",
    },
    label: {
      fontFamily: "EnterpriseSansVF, Arial, sans-serif",
      fontWeight: 400,
      color: "var(--color-accent)",
      fontSize: "23px",
      whiteSpace: "nowrap",
    },
    renderIcon: () => (
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          color: "var(--color-accent)",
          width: 32,
          height: 32,
          p: "4px",
          border: "1px solid var(--color-accent)",
          borderRadius: "32px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ExpandMoreIcon fontSize="small" />
      </Box>
    ),
  },
};

/** An option with a distinct underlying value and display label, e.g. an account id + its friendly name */
export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownSelectorProps {
  /** Accessible label for the trigger button, e.g. "Open tenant menu" */
  ariaLabel: string;
  /** Currently selected option, shown as the button text */
  selectedValue: string;
  /** Full list of selectable options. Plain strings are used as both value and label. */
  options: (string | DropdownOption)[];
  /** Called with the clicked option's value when a menu item is selected */
  onSelect: (option: string) => void;
  /** Optional data-testid applied to the selected-value text, for tests */
  valueTestId?: string;
  /** Visual style of the trigger button. Defaults to "outlined" (bordered pill, 32px). */
  variant?: DropdownSelectorVariant;
  /**
   * Trigger text shown when selectedValue has no matching option (e.g. label list still loading).
   * Defaults to selectedValue, which is correct when options are plain strings (value === label).
   * Pass "" when selectedValue is an internal id that should never be shown to the user raw.
   */
  fallbackLabel?: string;
}

/**
 * DropdownSelector - Shared button + menu used by header selectors (tenant, client, etc.)
 */
export const DropdownSelector: React.FC<DropdownSelectorProps> = ({
  ariaLabel,
  selectedValue,
  options,
  onSelect,
  valueTestId,
  variant = "outlined",
  fallbackLabel = selectedValue,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const styles = VARIANT_STYLES[variant];
  const normalizedOptions = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
  const selectedLabel = normalizedOptions.find((option) => option.value === selectedValue)?.label ?? fallbackLabel;

  return (
    <Box ref={containerRef} sx={{ display: "inline-flex" }}>
      <ButtonBase
        onClick={() => setAnchorEl(containerRef.current)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        sx={styles.trigger}
      >
        <Typography data-testid={valueTestId} sx={styles.label}>
          {selectedLabel}
        </Typography>
        {styles.renderIcon()}
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: "0px 4px 16px 0px var(--color-shadow-menu)",
              border: "1px solid var(--color-border-subtle)",
              p: 0,
              mt: "20px",
              minWidth: "275px",
            },
          },
        }}
      >
        {normalizedOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onSelect(option.value);
              setAnchorEl(null);
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{option.label}</span>
            {option.value === selectedValue && (
              <CheckIcon
                sx={{
                  fontSize: "20px",
                  color: "var(--color-ink)",
                  ml: 2,
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
